import { useCallback, useState } from "react";
import * as React from "react";
import { useParams } from "react-router-dom";

import { ExportMultipleCsv } from "components/export-multiple-csv";
import { Can } from "context/authz/can";
import { VerifyDialog } from "features/verify-dialog";
import type { TVerifyFn } from "features/verify-dialog/types";
import type { CsvReportFiltersInput } from "gql/graphql";
import { ReportType } from "gql/graphql";
import { useToeInputReport } from "pages/group/surface/inputs/hooks/use-data-input-report";
import { useToeLineReport } from "pages/group/surface/lines/hooks/use-data-lines-report";
import { Logger } from "utils/logger";

interface IExportButtonProps {
  isFiltered: boolean;
  reportType: ReportType;
  filters?: CsvReportFiltersInput;
}

const ExportButton = ({
  isFiltered = false,
  reportType,
  filters = undefined,
}: Readonly<IExportButtonProps>): JSX.Element => {
  const { groupName } = useParams() as { groupName: string };
  const [isVerifyDialogOpen, setIsVerifyDialogOpen] = useState(false);

  const handleClose = useCallback((): void => {
    setIsVerifyDialogOpen(false);
  }, [setIsVerifyDialogOpen]);

  const requestLineGroupReport = useToeLineReport(handleClose);
  const requestInputGroupReport = useToeInputReport(handleClose);

  const handleClick = useCallback(
    (setVerifyCallbacks: TVerifyFn): VoidFunction => {
      return (): void => {
        setIsVerifyDialogOpen(true);
        setVerifyCallbacks(
          async (verificationCode: string): Promise<void> => {
            if (reportType === ReportType.ToeLines) {
              await requestLineGroupReport({
                variables: {
                  filters: filters?.toeLinesFilters ?? undefined,
                  groupName,
                  verificationCode,
                },
              });
            } else if (reportType === ReportType.ToeInputs) {
              await requestInputGroupReport({
                variables: {
                  filters: filters?.toeInputsFilters ?? undefined,
                  groupName,
                  verificationCode,
                },
              });
            } else {
              Logger.warning(
                `Unexpected report type encountered: "${reportType}". Expected "ToeLines" or "ToeInputs". No request was sent.`,
                { filters, groupName, reportType },
              );
            }
          },
          (): void => {
            setIsVerifyDialogOpen(false);
          },
        );
      };
    },
    [
      filters,
      groupName,
      reportType,
      requestInputGroupReport,
      requestLineGroupReport,
    ],
  );

  return (
    <React.StrictMode>
      <Can do={"integrates_api_resolvers_query_csv_report_resolve"}>
        <VerifyDialog isOpen={isVerifyDialogOpen}>
          {(setVerifyCallbacks: TVerifyFn): JSX.Element => {
            return (
              <ExportMultipleCsv
                isFiltered={isFiltered}
                onClick={handleClick(setVerifyCallbacks)}
              />
            );
          }}
        </VerifyDialog>
      </Can>
    </React.StrictMode>
  );
};

export { ExportButton };
